import React, { useEffect, useCallback, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import DOMPurify from "dompurify";
import {
  Bold,
  Italic,
  Strikethrough,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Minus,
  Undo2,
  Redo2,
  Link2,
  Unlink,
} from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import {
  propertyMarketingGenerateDraft,
  propertyMarketingTranslateHtml,
} from "../../services/propertyMarketingAiService";

function MenuBar({ editor, t, readOnly }) {
  if (!editor || readOnly) return null;

  const ToolButton = ({ title, onClick, active, disabled, children }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={!!disabled}
      title={title}
      className={`rounded p-1.5 ${active ? "bg-gray-300 text-gray-900" : "bg-gray-100 text-gray-700 hover:bg-gray-200"} disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt(t("basicInfo.marketingLinkPrompt"), prev || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-gray-200 pb-2 mb-2" role="toolbar" aria-label="Formatting">
      <ToolButton
        title={t("basicInfo.marketingToolbarBold")}
        active={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="w-4 h-4" aria-hidden />
      </ToolButton>
      <ToolButton
        title={t("basicInfo.marketingToolbarItalic")}
        active={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="w-4 h-4" aria-hidden />
      </ToolButton>
      <ToolButton
        title={t("basicInfo.marketingToolbarStrike")}
        active={editor.isActive("strike")}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <Strikethrough className="w-4 h-4" aria-hidden />
      </ToolButton>
      <span className="w-px h-6 bg-gray-200 mx-0.5" aria-hidden />
      <ToolButton
        title={t("basicInfo.marketingToolbarH2")}
        active={editor.isActive("heading", { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 className="w-4 h-4" aria-hidden />
      </ToolButton>
      <ToolButton
        title={t("basicInfo.marketingToolbarH3")}
        active={editor.isActive("heading", { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 className="w-4 h-4" aria-hidden />
      </ToolButton>
      <span className="w-px h-6 bg-gray-200 mx-0.5" aria-hidden />
      <ToolButton
        title={t("basicInfo.marketingToolbarBulletList")}
        active={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="w-4 h-4" aria-hidden />
      </ToolButton>
      <ToolButton
        title={t("basicInfo.marketingToolbarOrderedList")}
        active={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="w-4 h-4" aria-hidden />
      </ToolButton>
      <ToolButton
        title={t("basicInfo.marketingToolbarQuote")}
        active={editor.isActive("blockquote")}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <Quote className="w-4 h-4" aria-hidden />
      </ToolButton>
      <ToolButton
        title={t("basicInfo.marketingToolbarHr")}
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      >
        <Minus className="w-4 h-4" aria-hidden />
      </ToolButton>
      <span className="w-px h-6 bg-gray-200 mx-0.5" aria-hidden />
      <ToolButton title={t("basicInfo.marketingToolbarLink")} onClick={setLink} active={editor.isActive("link")}>
        <Link2 className="w-4 h-4" aria-hidden />
      </ToolButton>
      <ToolButton
        title={t("basicInfo.marketingToolbarUnsetLink")}
        onClick={() => editor.chain().focus().extendMarkRange("link").unsetLink().run()}
        disabled={!editor.isActive("link")}
      >
        <Unlink className="w-4 h-4" aria-hidden />
      </ToolButton>
      <span className="w-px h-6 bg-gray-200 mx-0.5" aria-hidden />
      <ToolButton
        title={t("basicInfo.marketingToolbarUndo")}
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
      >
        <Undo2 className="w-4 h-4" aria-hidden />
      </ToolButton>
      <ToolButton
        title={t("basicInfo.marketingToolbarRedo")}
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
      >
        <Redo2 className="w-4 h-4" aria-hidden />
      </ToolButton>
    </div>
  );
}

export default function MarketingDescriptionSection({ property, onChange, readOnly = false }) {
  const { t, language } = useLanguage();
  const { userProfile } = useAuth();
  const [translationPreview, setTranslationPreview] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState("");
  const isCourtierImmo = userProfile?.persona === "courtier_immo";

  const pushHtml = useCallback(
    (html) => {
      const clean = DOMPurify.sanitize(html || "", { USE_PROFILES: { html: true } });
      onChange({ ...property, marketingDescriptionHtml: clean });
    },
    [onChange, property]
  );

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({ heading: { levels: [2, 3] } }),
        Link.configure({
          openOnClick: false,
          autolink: true,
          linkOnPaste: true,
          HTMLAttributes: {
            rel: "noopener noreferrer",
            class: "text-blue-700 underline decoration-blue-600/60",
          },
        }),
        Placeholder.configure({
          placeholder: t("basicInfo.marketingEditorPlaceholder"),
        }),
      ],
      editable: !readOnly,
      content: property.marketingDescriptionHtml || "<p></p>",
      onUpdate: ({ editor: ed }) => {
        const raw = ed.getHTML();
        const clean = DOMPurify.sanitize(raw, { USE_PROFILES: { html: true } });
        onChange({ ...property, marketingDescriptionHtml: clean });
      },
    },
    [property.id, language, readOnly]
  );

  useEffect(() => {
    if (!editor) return;
    const next = property.marketingDescriptionHtml || "";
    editor.commands.setContent(next || "<p></p>", { emitUpdate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- align with property change only (see useEditor deps)
  }, [property.id, editor]);

  useEffect(() => {
    if (editor) editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  const handleGenerate = async () => {
    if (!property.id || !isCourtierImmo) return;
    setAiError("");
    setAiBusy(true);
    try {
      const instructionHtml = DOMPurify.sanitize(editor?.getHTML() || "", { USE_PROFILES: { html: true } });
      const { html } = await propertyMarketingGenerateDraft({
        propertyId: property.id,
        locale: language,
        instructionHtml,
      });
      const clean = DOMPurify.sanitize(html || "", { USE_PROFILES: { html: true } });
      editor?.commands.setContent(clean || "<p></p>", { emitUpdate: false });
      pushHtml(clean);
    } catch (e) {
      setAiError(e?.message || String(e));
    } finally {
      setAiBusy(false);
    }
  };

  const handleTranslate = async (targetLang) => {
    if (!isCourtierImmo) return;
    const src = editor?.getHTML() || property.marketingDescriptionHtml || "";
    if (!String(src).replace(/<[^>]+>/g, "").trim()) return;
    setAiError("");
    setAiBusy(true);
    setTranslationPreview("");
    try {
      const { html } = await propertyMarketingTranslateHtml({ html: src, targetLang });
      setTranslationPreview(DOMPurify.sanitize(html || "", { USE_PROFILES: { html: true } }));
    } catch (e) {
      setAiError(e?.message || String(e));
    } finally {
      setAiBusy(false);
    }
  };

  const applyTranslation = () => {
    if (!translationPreview) return;
    editor?.commands.setContent(translationPreview, { emitUpdate: false });
    pushHtml(translationPreview);
    setTranslationPreview("");
  };

  if (!editor) {
    return (
      <div className="border rounded-lg p-4 sm:p-6">
        <p className="text-sm text-gray-500">{t("loading")}</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-4 sm:p-6">
      <h2 className="text-lg font-semibold mb-2 text-blue-600">{t("basicInfo.marketingTitle")}</h2>
      <p className="text-sm text-gray-600 mb-2">{t("basicInfo.marketingHint")}</p>
      {isCourtierImmo && (
        <div className="flex flex-wrap gap-2 mb-3">
          <button
            type="button"
            disabled={aiBusy || !property.id || readOnly}
            onClick={handleGenerate}
            className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {aiBusy ? t("basicInfo.marketingAiLoading") : t("basicInfo.marketingAiGenerate")}
          </button>
          <button
            type="button"
            disabled={aiBusy || readOnly}
            onClick={() => handleTranslate("en")}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {t("basicInfo.marketingAiTranslateEn")}
          </button>
          <button
            type="button"
            disabled={aiBusy || readOnly}
            onClick={() => handleTranslate("fr")}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {t("basicInfo.marketingAiTranslateFr")}
          </button>
        </div>
      )}
      {isCourtierImmo && <p className="text-xs text-gray-500 mb-2">{t("basicInfo.marketingAiDisclaimer")}</p>}
      {aiError && <p className="text-sm text-red-600 mb-2">{aiError}</p>}
      <div
        className={`prose prose-sm max-w-none border rounded-md p-3 min-h-[160px] bg-white focus-within:ring-2 focus-within:ring-blue-200 focus-within:border-blue-300 ${
          readOnly ? "bg-gray-50" : ""
        }`}
      >
        <MenuBar editor={editor} t={t} readOnly={readOnly} />
        <EditorContent editor={editor} />
      </div>
      {translationPreview && (
        <div className="mt-4 border border-dashed border-indigo-200 rounded-lg p-3 bg-indigo-50/40">
          <p className="text-sm font-medium text-indigo-900 mb-2">{t("basicInfo.marketingTranslationPreview")}</p>
          <div
            className="prose prose-sm max-w-none text-gray-800 mb-3"
            dangerouslySetInnerHTML={{ __html: translationPreview }}
          />
          <button
            type="button"
            onClick={applyTranslation}
            className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            {t("basicInfo.marketingAiApply")}
          </button>
        </div>
      )}
    </div>
  );
}
